import type { AuthProvider } from "../auth/types.js";
import { type FeatureId, normalizeFeatures } from "../config/features.js";

export interface SecondaryResourceCheck {
  url: string;
  label: string;
  features: FeatureId[];
  critical: boolean;
  nextStep: string;
}

export interface SecondaryResourceResult extends SecondaryResourceCheck {
  ok: boolean;
  message?: string;
}

export const SECONDARY_RESOURCE_CHECKS: SecondaryResourceCheck[] = [
  {
    url: "https://graph.microsoft.com",
    label: "Microsoft Graph (SharePoint/Excel)",
    features: ["graph"],
    critical: true,
    nextStep:
      "Add Microsoft Graph delegated permissions User.Read, Sites.ReadWrite.All, and Files.ReadWrite.All, grant admin consent, then re-run: powerautomate-mcp --setup",
  },
  {
    url: "https://service.powerapps.com",
    label: "PowerApps Service (connections/Power Apps)",
    features: ["powerApps"],
    critical: true,
    nextStep:
      "Add PowerApps Service (475226c6-020e-4fb2-8a90-7a972cbfc1d4) delegated permission User, grant admin consent, then re-run: powerautomate-mcp --setup",
  },
  {
    url: "https://api.bap.microsoft.com",
    label: "BAP Admin API (environments/Dataverse)",
    features: ["dataverse", "admin", "powerPages", "powerPagesManagement"],
    critical: true,
    nextStep:
      "Add BAP Admin API (0e0bf3cc-3078-4fd4-9ef3-cb6dc0245b10) delegated permission user_impersonation, grant admin consent, then re-run: powerautomate-mcp --setup",
  },
  {
    url: "https://api.powerplatform.com",
    label: "Power Platform API (Copilot Studio / Power Pages management)",
    features: ["dataverse", "powerPagesManagement"],
    critical: false,
    nextStep:
      "Required for Copilot Studio inventory, authenticated execution, evaluations, governance, and optional Tier 2 Power Pages management. Exact steps: " +
      "(1) ensure the API's service principal exists: az ad sp create --id 8578e004-a5c6-46e7-913e-12f58912df43 (ignore 'already exists'); " +
      "(2) in Entra portal > App registrations > your app > API permissions > Add a permission > APIs my organization uses > 'Power Platform API' > Delegated, select ResourceQuery.Resources.Read, CopilotStudio.Copilots.Invoke, CopilotStudio.MakerOperations.Read, CopilotStudio.MakerOperations.ReadWrite, and CopilotStudio.AdminActions.Invoke (plus PowerPages.Websites.Read and PowerPages.Websites.Write for Power Pages management); " +
      "(3) grant admin consent: az ad app permission admin-consent --id <your-clientId>; " +
      "(4) refresh the signed-in session or restart the MCP server so it obtains a token carrying the new consent; re-running --setup cannot grant admin consent",
  },
];

export async function checkSecondaryResourceAuth(
  authProvider: AuthProvider,
  features?: readonly FeatureId[],
): Promise<SecondaryResourceResult[]> {
  const results: SecondaryResourceResult[] = [];
  const enabled = new Set(normalizeFeatures(features));

  for (const check of SECONDARY_RESOURCE_CHECKS) {
    if (!check.features.some((feature) => enabled.has(feature))) {
      continue;
    }

    try {
      const silentToken = authProvider.tryGetTokenForResourceSilent
        ? await authProvider.tryGetTokenForResourceSilent(check.url)
        : null;
      if (!silentToken) {
        await authProvider.getTokenForResource(check.url);
      }
      results.push({ ...check, ok: true });
    } catch (err) {
      results.push({
        ...check,
        ok: false,
        message: summarizeError(err),
      });
    }
  }

  return results;
}

function summarizeError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const cleaned = msg.replace(/\s+/g, " ").trim();
  const firstSentence = cleaned.match(/^.*?\.(?:\s|$)/)?.[0]?.trim();
  const summary = firstSentence && firstSentence.length >= 40 ? firstSentence : cleaned;
  return summary.length > 260 ? `${summary.slice(0, 257).trim()}...` : summary;
}
